import { Agent } from '../../../../common/js/customer/Agent';
import { AgentShareController } from '../controllers/AgentShareController';
import { CoverpathControllerInterface } from '../../../../coverpath-common/angular/interfaces/controller/CoverpathControllerInterface';
import { CoverpathCustomer } from '../../../../coverpath-common/classes/coverpath_subclasses/CoverpathCustomer';
import { AccountCenterController } from '../controllers/AccountCenterController';
import { PolicyAgent } from '../../../../common/js/customer/PolicyAgent';
import { Emailer } from '../../../../common/js/services/emailer/Emailer';
import { AgentForProxyDTO } from '../../../../common/js/agent/AgentForProxyDTO';
import { AuthPayload } from '../../../../common/js/interfaces/AuthPayload';
import { UserPrincipal } from '../../../../common/js/agent/UserPrincipal';
import { PolicyRider } from '../../../../common/js/customer/PolicyRider';
import { PolicyAdminStatus } from '../../../../common/js/policyAdmin/PolicyAdminStatus';
import { QuestionnaireServiceController } from '../controllers/QuestionnaireServiceController';
import { ApplicationStages } from '../static/AnnuityStatusTypes';
import { EventorElasticSearchController } from '../controllers/EventorElasticSearchController';
import { UnderwriterDetail } from '../../../../common/js/uwService/Types';
import { AccepttoController } from '../controllers/AccepttoController';
import { ProducerGroup } from '../../../../common/js/customer/ProducerGroup';
import { Address, TPolicyAdminPartyInfo } from '../../../../common/js/interfaces/CommonTypes';
import { ApplicationSubmissionController } from '../controllers/ApplicationSubmissionController';
import { Entitlement } from '../../../../common/js/types/CommonTypes';

export { Entitlement } from '../../../../common/js/types/CommonTypes';
import { PolicyAdminClient } from '@hit/policy-admin-client';
import { PolicyAdminRiders } from '../../../../common/js/policyAdmin/PolicyAdminRiders';
import { CaseDetailsController } from '../controllers/CaseDetailsController';

//todo: HL-6905 @nick why do we have this extra Controller type? Why not just the agent Controller? - Question From Jasmine...
export interface AgentControllerInterface {
  customer: CoverpathCustomer;
  agentShareController: AgentShareController;
  applicationSubmissionController: ApplicationSubmissionController;
  accountCenterController: AccountCenterController;
  accepttoController: AccepttoController;
  caseDetailsController: CaseDetailsController;
  questionnaireServiceController: QuestionnaireServiceController;
  eventorElasticSearchController: EventorElasticSearchController;
  logout: () => void;
  processLogin: (redirect: RedirectResponse, authPayLoad: AuthPayload) => Promise<RedirectResponse>;
  emailer: Emailer;
  sendToSegment: boolean;
  segmentKey: string;
  proxyEnabled: boolean;
  clientInviteUrl: string;
  clientConversionInviteUrl: string;
  proxyingAsAgent: Agent; // If proxy logged in, this is the agent they are proxying as
  loggedInPrincipal: UserPrincipal; // logged in principal user
  hasProxyLoggedIn: () => boolean;
  hasWholesaleProxyLoggedIn: () => boolean;
  hasDualRoleUserLoggedIn: () => boolean;
  hasProxyEntitlement: () => boolean;
  isInProxyMode: (primaryAgentProducerId?: string) => boolean;
  getAgent: () => Agent;
  nbDecommission: boolean;
  nbDecommissionBanner: boolean;
  nbDecommissionBannerText: string;
}

//todo: HL-6905 @nick we may need to think about to rename this type?
export type AgentController = AgentControllerInterface & CoverpathControllerInterface;

export enum InviteEmailType {
  'GENERIC',
  'PERSONALIZED',
  'QUOTE',
  'ADVISOR_ASSISTED',
  'ADVISOR_EDIT',
  'CONVERSION',
}

export type MailToInfo = {
  invitationType: InviteEmailType;
  agentEmail: string;
  agentFirstName: string;
  inviteLink: string;
  showPremiumToClient?: boolean;
  clientEmail?: string;
  clientFirstName?: string;
  term?: number;
  termFaceAmount?: number;
  wholeFaceAmount?: number;
  termPremium?: number;
  wholePremium?: number;
  termPaymentFrequency?: string;
  wholePaymentFrequency?: string;
  productType?: string;
  isJointApp?: boolean;
  ownerEqualsInsured?: boolean;
  insuredFullName?: string;
  wholeTerm?: number;
};

export type ClientPolicyStatus = {
  summaryStatus: string; // one glance status of a policy in flight
  subStatus: string; // detailed status of a policy
  isPostApp: boolean; // indicates if the policy has been submitted
  isPostIssue: boolean; // indicates if the policy is after In Force and managed by Policy Admin
  isInParamed: boolean; // indicates if the policy is during paramed (MA3 or TC3)
  isInUnderwriting: boolean; // indicates if the policy is in Underwriting
  important?: boolean; // if true, UI will display subStatus in red
  actionRequiredDate?: Date; // a deadline date when certain action has to be taken such as client esign
  importantIfLessThanDays?: number; // number of days until the deadline for UI to display subStatus in red
  agentEsignRequired?: boolean; // does the agent need to esign any forms
  stageBillingStatus?: string; // Policy Admin stage and billing combination code, and its used to decide if policy packet is available
  tfaPhone?: string; // the client's phone
  tfaStatus?: string; // TFA status such as if the phone has been TFA verified
  progressPercentage?: number; //total application finished progress percentage
  workflowState?: string;
  workflowSubState?: string;
  holdTime?: Date;
  conversionEndDate?: Date;
  conversionCreditPercent?: number;
  conversionCreditExpirationDate?: Date;
  acceleratedBenefitRider?: boolean;
  nextPaymentDate: Date;
  paidToDate: Date;
  effectiveDate: Date;
  tlicEffectiveDate: Date;
  freeLookEndDate: Date;
  rateClass?: string;
  hasWaiverOfPremium?: boolean;
  faceAmount?: number;
  paTerm?: number;
  paTotalPremium?: number;
  paPaymentFrequency?: string;
  paPolicyId?: string;
  paCaseDetailsStatus?: string;
  graceEndDate?: Date;

  // @TODO remove this once cp starts using party data from eventor
  paOwners?: Array<TPolicyAdminPartyInfo>;
  paInsured?: TPolicyAdminPartyInfo;
  paRiders?: PolicyAdminRiders;
  paIsOwnerEntity?: boolean;

  timezone?: string;
  timezoneAbbr?: string;
  lockedBy?: string;
  isFrozen?: string;
  appMetadataVersion?: number;
  uwStatusDate?: Date;
  applicationSignedDate?: Date;
  issuedDate?: Date;
  nextStatus?: string;
  underwriter?: UnderwriterDetail;
  issuePolicyRequestDate?: Date;
  term?: number;
  totalPremium?: number;
  policyNumber?: string;
  paymentMethod?: string;
  isAgentSubmittedApp?: boolean;
  paymentFrequency?: string;
};

export type ClientAnnuityContractStatus = ClientPolicyStatus & {
  applicationStage?: ApplicationStages;
  actions?: Array<string>; // a list of actions that can taken depending on the state in which this policy is in
  overrides?: {
    // used to carry data that do not come from HIT db
    ownerFullName: string;
    initialPurchaseAmount: number;
  };
};

export type PersonProps = {
  email: string;
  firstName: string;
  lastName: string;
  progress?: number;
  tfaPhone?: string;
};

export type AccountCenterListRecordResult = {
  clients: Array<AccountCenterListRecordView>;
  resultsCount: number;
};

export type SiblingApplication = {
  defaultPolicyInfo?: {
    policyNumber: string;
    faceAmount: string;
    product: string;
    term: string;
    productType: string;
    amendedProductType: string;
    amendedFaceAmount: string;
    amendedProduct: string;
    amendedTerm: string;
    amendedType: string;
    workflowState: string;
    isTermToJointAmendment: boolean;
    type: string;
    paFaceAmount?: string;
    isPostIssue?: boolean;
  };
  secondaryPolicyInfo?: {
    policyNumber: string;
    faceAmount: string;
    product: string;
    term: string;
    productType: string;
    amendedProductType: string;
    amendedFaceAmount: string;
    amendedProduct: string;
    amendedTerm: string;
    amendedType: string;
    workflowState: string;
    isTermToJointAmendment: boolean;
    type: string;
    paFaceAmount?: string;
    isPostIssue?: boolean;
  };
  encodedAppId: string;
  insuredNameDisplay: string;
};

export type Designation = string;

export type AgentInfo = {
  producerId: string;
  displayName?: string;
  firstLetter?: string;
  firstName?: string;
  lastName: string;
  phoneNumber?: string;
  emailAddress?: string;
  designations?: Array<Designation>;
  isActive?: boolean;
  primaryProducerSplit?: number;
  dbId?: string;
};

export type InsuredName = {
  first: string;
  middle: string;
  last: string;
};

export type AccountCenterListRecordView = {
  policyId: string;
  policyNumber: string;
  secondaryPolicyNumber?: string;
  product: string;
  productType: string;
  status: ClientPolicyStatus;
  ownerName: string;
  ownerNameDisplay: string;
  ownerDOB?: string;
  ownerPhone?: string;
  ownerSSN?: string;
  ownerEmail?: string;
  ownerContractState?: string;
  insuredName: string;
  insuredNameComplete: InsuredName;
  insuredNameDisplay: string;
  insuredDOB?: string;
  insuredGender?: string;
  insuredOccupation?: string;
  insuredSSN?: string;
  insuredPhone?: string;
  insuredEmail?: string;
  insuredMailingAddress?: string;
  insuredStateOfBirth?: string;
  insuredCountryOfBirth?: string;
  isOwnerSameAsInsured: boolean;
  accountholderEmail?: string;
  tLICStatus?: string;
  face: number;
  term: number;
  lastUpdatedDate: Date;
  policyCreatedTime?: Date;
  applicationNumber: string;
  applicationId: string;
  primaryAgentInfo: AgentInfo; // this relies on "policy"."primaryPolicyAgent_id"
  primaryAgentRecord?: AgentInfo; // this relies on "policy_agent"."policy_id" and policy_agent.type
  servicingAgentRecord?: AgentInfo;
  additionalAgentRecords?: Array<AgentInfo>;
  secondaryPolicyDetails?: {
    policyId: string;
    policyNumber: string;
    faceAmount: number;
    product: string;
    term: number;
    premium?: number;
    amendedType?: string;
    amendedTerm?: number;
    amendedFaceAmount?: number;
    amendedProduct?: string;
    isTermToJointAmendment: boolean;
    status: ClientPolicyStatus;
    //Conversion details
    convertedTo: string;
  }; //json
  coverpathCaseUrl?: string;
  otherProducersID?: string[]; //to be added
  otherProducersSplit?: Array<{ [producerId: string]: number }>; //to be added
  issueDate?: Date;
  applicationSignedDate: Date; //client esigned(docusign) date
  customerEmail: string;
  inviteCode: string;
  replaceInforceApplication?: boolean; //for by pass agent esign replacement form only
  policyDocReady?: boolean;
  progressPercentage?: number;
  workflowName?: string;
  amendedTerm?: number;
  amendedFaceAmount?: number;
  amendedType?: string;
  amendedProductType?: string;
  amendedProduct?: string;
  premium?: number;
  paymentFrequency?: string;
  effectiveDate?: Date;
  submittedDate: Date;
  paramedScheduledDate: Date;
  paramedRequiredBy: Date;
  convertedFrom: string;
  convertedTo: string;
  applicationState?: string;
  insruedResidentialZip?: string;
  otherOwnerName?: string;
  isDocuSignedFormRetrieved?: boolean;
  finalOfferSentDate?: Date;
  finalOfferAcceptedDate?: Date;
  trustSitus?: string;
  isLoggedInPrincipalFollowing: boolean;
  isLiteTouch: boolean;
  followers?: Array<Follower>;
  alir1035Purpose?: string;
  alir1035FundsReceivedDate?: Date;
  agentEsignCompleteDateUw?: Date;
  isAgentSubmittedApp?: boolean;
  caseFilterStatus?: string;
  ownerNameDisplayWithMiddle?: string;
  insuredNameDisplayWithMiddle?: string;
  examOptOut?: boolean;

  // AccountCenterCaseExportRecordView
  insuredIDType?: string;
  insuredIDNumber?: string;
  insuredIDIssueState?: string;
  insuredIDExpiryDate?: string;
  insuredCitizenshipType?: string;
  insuredEmployerName?: string;
  insuredAnnualIncome?: string;
  insuredNetWorth?: string;
};

export type AnnuityAccountCenterListRecordView = AccountCenterListRecordView & {
  status: ClientAnnuityContractStatus;
  annuitantNameDisplay?: string;
  isOwnerSameAsAnnuitant?: boolean;
  applicationFormQsID?: string;
  suitabilityFormQsID?: string;
  fR3013FormQsID?: string;
  replacementFormQsID?: string;
  cotFormQsID?: string;
};

export enum PolicyTypes {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  CONVERTED = 'converted',
}

export enum PolicyTitles {
  APPLICATION = 'Application',
  POLICY = 'Policy',
}

export enum UWActivityDisplayLevel {
  ALL = 'all',
  NONE = 'none',
  UW_ACTIVITY = 'uw_activity',
  FOLLOWUPS = 'followups',
  ALL_WITH_APS = 'all_with_aps',
}

export type AccountCenterDetailedRecordView = AccountCenterListRecordView & {
  policyType: PolicyTypes;
  policyTitle: PolicyTitles;
  ownerAddress?: Address;
  ownerPhone?: string;
  basePremium: number;
  tlicBase: number;
  insuredAddress: Address;
  insuredPhone: string;
  commission: Array<number>;
  commissionSplit: Array<PolicyAgent>;
  primaryAgent: PolicyAgent;
  waiverAmount: number;
  tlicWaiver: number;
  feTotal: number;
  tlicFeTotal: number;
  policyPaymentFrequency: string;
  quotePremium?: number;
  hasWaiverOfPremium?: boolean;
  hasWaiverOfPremiumInQuote?: boolean;
  waiver?: number;
  tfaPhone?: string;
  tfaStatus?: string;
  dividendOption?: string;
  loanInterestType?: string;
  wholeAPL?: boolean;
  isAAA: boolean;
  workflowState: string;
  workflowSubState: string;
  AMCuserStatus: string;
  hasCreatedAccount: boolean;
  otherOwnerName?: string;
  otherOwnerAddress?: Address;
  otherOwnerPhone?: string;
  otherOwnerEmail?: string;
  otherOwnerDOB?: string;
  firstOwner?: { name: string; phone: string; address: Address; isOwnerSameAsInsured: boolean };
  otherOwner?: { name: string; email: string; phone: string; address: Address; isOwnerSameAsInsured: boolean };
  isPartOfJointApp?: boolean;
  paAllowAddressChange?: boolean;
  paIsOwnerEntity?: boolean;
  paHasWaiverOfPremium: boolean;
  paRateClass: string;
  paFaceAmount: number;
  paEffectiveDate?: Date;
  paTerm?: number;
  paTotalPremium?: number;
  paPaymentFrequency?: string;
  paPolicyId?: string;
  paConversionCredit?: number;
  paConversionCreditExpirationDate?: Date;
  paAcceleratedBenefitRider?: boolean;
  conversionEndDate: Date;
  inForce: boolean;
  has1035Exchange: boolean;
  exchangePolicyStatusAndValue: string;
  riders: Array<PolicyRider>;
  appMetadataVersion?: number;
  isSociety1851?: boolean;
  amendedDividendOption?: string;
  amendedWholeAPL?: boolean;
};

export type AccountCenterAnnuityDetailedRecordView = AccountCenterDetailedRecordView &
  AnnuityAccountCenterListRecordView;

export type MockLoginPayload = {
  submitUrl: string;
  saml: any;
};

export type InvitationEmailSubjectAndBody = {
  subject: string;
  body: string;
};

export type EmailProps = InvitationEmailSubjectAndBody & {
  to: string;
};

export type CommissionSplit = {
  producerId: string;
  displayName: string;
  ratio: number;
  renewalCommissionRatio: number;
};

export const jointAppRegExp: RegExp = new RegExp('\\+jointapp');
export const brokerCorpsRegExp: RegExp = new RegExp('\\+brokercorps');
export const commissionSplit: RegExp = new RegExp('\\+splitcommission');

export enum ActionablePolicyStatus {
  DraftAppStarted = 'Draft App Started',
  AnnuityAppStarted = 'Application started',
}

export interface AdvisorWithFullName {
  fullName: string;
  selectionId: string;
  advisor: AgentForProxyDTO;
  producerGroup: ProducerGroup;
}

export interface AdvisorListGroup {
  listWithAccess: AdvisorWithFullName[];
  listWithNoAccess: AdvisorWithFullName[];
  producerGroups: AdvisorWithFullName[];
}

export type EsignDocumentSummary = {
  displayName: string;
  documentName: string;
  triggerAction: string;
  isActive: boolean;
};

export type EsignDocumentSummaryObjects = { [key: string]: EsignDocumentSummary };

export type AppSigningStatus = {
  policyNumber: string;
  isSigning: boolean;
  isSigned: boolean;
  isError: boolean;
  isRBI: boolean;
  isConversion?: boolean;
};

export type AccountCenterSearchParameters = {
  policyNumber: string;
  ownerEmail: string;
  customerEmail: string;
  ownerBusinessName: string;
  ownerTrustName: string;
  ownerFullNameAlphaNumeric: string;
  insuredFullNameAlphaNumeric: string;
  advisorLastNameAlphaNumeric: string;
  hasMultipleAdvisors: boolean;
  includeAdvisorNameInKeywordSearch: boolean;
};

export type AccountCenterTeleSearchParameters = {
  policyNumber: string;
  ssn: string;
  insuredFullNameAlphaNumeric: string;
};

export type WholeLifePremiumFields = {
  dob: string;
  gender: string;
  healthClass: string;
  contractState: string;
  product: string;
  faceAmount: number;
  paymentFrequency: string;
  dividendOption: string;
  loanType: string;
};

export type TermPremiumFields = {
  dob: string;
  gender: string;
  healthClass: string;
  contractState: string;
  coverageTerm: string;
  faceAmount: number;
  paymentFrequency: string;
};

/**
 * Defines the format for an action
 * like a button click or a link click (for example kebab menu)
 */

export enum ActionTypes {
  DELETE = 'delete',
  COPY_LINK = 'copyLink',
  VIEW_APPLICATION = 'viewApplication',
  COPY_APPLICATION = 'copyApplication',
  EDIT_APPLICATION = 'editApplication',
  EDIT_SUITABILITY_FORM = 'editSuitabilityForm',
  EDIT_FR3013_FORM = 'editFR3013Form',
  EDIT_REPLACEMENT_FORM = 'editReplacementForm',
  EDIT_CERTIFICATE_OF_TRUST_FORM = 'editCertificateOfTrustForm',
  VIEW_SUITABILITY_FORM = 'viewSuitabilityForm',
  VIEW_FR3013_FORM = 'viewFr3103Form',
  VIEW_REPLACEMENT_FORM = 'viewReplacementForm',
  VIEW_CERTIFICATE_OF_TRUST_FORM = 'viewCertificateOfTrustForm',
  CANCEL = 'Cancel',
  GENERATE_ILLUSTRATION_DOC = 'generateIllustrationDoc',
  FOLLOWING = 'following',
}

export type ActionDefinition = {
  actionType: ActionTypes;
  displayText: string;
  subText?: string;
  icon?: string;
};

export type POST_ISSUE_STATUS_MAP_Format = {
  [stageName: string]: {
    status: string;
    subStatus: ((policyConvertedTo: string, policyConvertedToIsInForce: boolean) => string) | string;
    actionRequiredDate?: (paStatus: PolicyAdminStatus) => Date;
    important?: boolean;
    importantIfLessThanDays?: number;
    nextStatus?: ((policyConvertedTo: string) => string) | string;
  };
};

export enum CASE_STATUS {
  ALL = 'All Cases',
  PRESUBMIT = 'Pre-Submit',
  PENDING = 'Pending',
  APPROVED = 'Approved',
  DECLINED = 'Declined',
  ISSUED = 'Issued',
  CANCELED = 'Canceled',
}

export enum TELE_CASE_STATUS {
  ACTIVE = 'Active',
  COMPLETED = 'Completed',
  CLOSED = 'Closed',
}

/**
 * current producer = "logged in producer" or "selected producer from the producer filter"
 */
export enum SPLIT_VISIBILITY_OPTIONS {
  ALL_CASES = 'ALL_CASES', // cases cases where current producer is primary or secondary
  PRIMARY_CASES = 'PRIMARY_CASES', // cases where current producer is primary
}

export type CaseStatusCounts = {
  [CASE_STATUS.ALL]: number;
  [CASE_STATUS.PRESUBMIT]: number;
  [CASE_STATUS.PENDING]: number;
  [CASE_STATUS.APPROVED]: number;
  [CASE_STATUS.DECLINED]: number;
  [CASE_STATUS.ISSUED]: number;
  [CASE_STATUS.CANCELED]: number;
};
export type CaseCounts = {
  // Map to case status counts in the side bar on the left part of list view in agent account center
  caseStatusCounts: CaseStatusCounts | TeleCaseStatusCounts;
  // Map to the count next to the following check box in the side bar on the left part of list view in agent account center
  following: number;
  // Map to the count next to the Society 1851 check box in the side bar on the left part of list view in agent account center
  society1851: number;
  isUrgent: number;
  splitVisibility: {
    allCases: number;
    primaryCases: number;
    hasSecondaryCases?: boolean; // count unfiltered cases
  };
};

export type TeleCaseStatusCounts = {
  [TELE_CASE_STATUS.ACTIVE]: number;
  [TELE_CASE_STATUS.COMPLETED]: number;
  [TELE_CASE_STATUS.CLOSED]: number;
};

export type Follower = {
  email: string;
  displayName?: string; // ideal but doesn't always exist
  firstName: string;
  lastName: string;
  userid?: string;
};

export enum CASE_TRACKER_STAGE {
  application = 'application',
  initialDecision = 'initialDecision',
  signature = 'signature',
  exam = 'exam',
  underwriting = 'underwriting',
  offer = 'offer',
  issuance = 'issuance',
}

export enum CASE_TRACKER_STATUS {
  NOT_STARTED = 'not started',
  SKIPPED = 'skipped',
  IN_PROGRESS = 'in progress',
  COMPLETE = 'complete',
  COMPLETE_ANIMATED = 'complete animated',
}

export type CaseTrackerStatus = {
  [CASE_TRACKER_STAGE.application]: CASE_TRACKER_STATUS;
  [CASE_TRACKER_STAGE.initialDecision]: CASE_TRACKER_STATUS;
  [CASE_TRACKER_STAGE.signature]: CASE_TRACKER_STATUS;
  [CASE_TRACKER_STAGE.exam]: CASE_TRACKER_STATUS;
  [CASE_TRACKER_STAGE.underwriting]: CASE_TRACKER_STATUS;
  [CASE_TRACKER_STAGE.offer]: CASE_TRACKER_STATUS;
  [CASE_TRACKER_STAGE.issuance]: CASE_TRACKER_STATUS;
};

export enum WORKFLOW_ROUTES {
  FLUIDLESS = 'fluidless',
  TLIC = 'tlic',
  KNOCKOUT = 'knockout',
}

export type CaseTrackerStageConfiguration = {
  title: string;
  imageUrl: string;
  summary: string[];
  previous: CASE_TRACKER_STAGE;
  next: CASE_TRACKER_STAGE;
  tabs?: {
    [key: string]: string[];
  };
  component: string;
};

export type ConceptIllustrationVM = {
  _id: string;
  taxBracket: number;
  faceAmount: number;
  product: string;
  productDescription: string;
  startAge: number;
  endAge: number;
  healthClass: string;
  dividend: string;
  dividendInterestAdjustment: number;
  paymentFrequency: string;
  loanType: string;
  insured: ConceptIllustrationInsuredVM;
  incomeOption: string;
  waiverOfPremium: boolean;
  loanInterestType: string;
  disbursementsToggle: boolean;
  lastUpdatedTime: Date;
  producerName: string;
};

export type ConceptIllustrationForListView = {
  _id: string;
  faceAmount: number;
  product: string;
  name: string;
  producerId: string;
  proxyId: string;
  lastUpdatedTime: Date | string;
};

export type ConceptIllustrationInsuredVM = {
  _id: string;
  firstName: string;
  lastName: string;
  name: string;
  state: string;
  gender: string;
  dateOfBirth: string;
};

export type CaseTrackerListItem = {
  stage: CASE_TRACKER_STAGE;
  status: CASE_TRACKER_STATUS;
};

export enum CASE_TRACKER_SKIPPED_TEXT {
  signature = 'The initial signature wasn’t needed because the client received an Express Path offer.',
  exam = 'The exam was skipped because the client received an Express Path offer.',
  underwriting = 'Manual underwriting was skipped because the client received an Express Path offer.',
}

export enum Partner_GENDER {
  M = '1',
  Male = '1',
  male = '1',
  F = '2',
  Female = '2',
  female = '2',
}

export type PartnerIllustrationData = {
  ownerFirstName: string;
  ownerLastName: string;
  contractState: string;
  insuredFirstName: string;
  insuredLastName: string;
  dob: Date;
  gender: string;
  ownerEqualsInsured: boolean;
};

export enum LIST_VIEW_TABS {
  LIFE = 'LIFE',
  ANNUITIES = 'ANNUITIES',
}

export enum TIME_FILTER_OPTIONS {
  DAY = '24hr',
  SEVEN_DAY = '7day',
  SIX_MONTH = '6mo',
  ALL = 'all',
}

// import { ControllerResponse } from '@haventech/amorphic'; // @TODO make this work
export type RedirectResponse = {
  status: number;
  headers?: {
    location: string;
  };
};

export type DataResponse = {
  status: number;
  body?: string;
};

export type ControllerResponse = RedirectResponse | DataResponse;

// add producerGroups type
export type ProducerGroupSharee = {
  _id: string;
  entitlement: Entitlement;
  displayName: string;
  producerId: string;
  email: string;
  lastName: string;
};

export type ProducerGroupChoiceMember = {
  _id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  displayName: string;
  producerId: string;
  disabled: boolean;
  checked: boolean;
};

export type SelectedProducerGroupChoice = {
  fullName: string;
  selectionId: string;
  advisor: ProducerGroupChoiceMember;
  disabled: boolean;
  checked: boolean;
};

export type AgentRecordForListViewQueryParam = {
  id: string;
  agencyId: string;
};
